import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import type { SaveActivityGroupRequest, SaveActivityGroupResponse, ValidationErrorResponse } from '../../../types/activity';
import type { ActivityType } from '../../../types/activityType';
import { activityService } from '../../../services/activityService';

import type { ActivityFormData } from './types';
import { generateId } from './types';

interface UseActivityGroupSubmitParams {
    companyId: number;
    groupBid?: number;
    activities: ActivityFormData[];
    activityTypes: ActivityType[];
    groupDescription: string;
    paidByClientAmount?: number;
    paidToProviderAmount?: number;
    operationTypesLoaded: boolean;
    storageKey: string;
    onSuccess: (response: SaveActivityGroupResponse) => void;
}

export function useActivityGroupSubmit({
    companyId,
    groupBid,
    activities,
    activityTypes,
    groupDescription,
    paidByClientAmount,
    paidToProviderAmount,
    operationTypesLoaded,
    storageKey,
    onSuccess,
}: UseActivityGroupSubmitParams) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validationLoading, setValidationLoading] = useState(false);
    const [activityServerErrors, setActivityServerErrors] = useState<Record<string, string[]>>({});
    const [generalServerErrors, setGeneralServerErrors] = useState<string[]>([]);

    const buildSaveRequest = useCallback((): SaveActivityGroupRequest => ({
        activityGroup: {
            rid: groupBid ? `group-${groupBid}` : generateId(),
            bid: groupBid,
            companyId,
            description: groupDescription || undefined,
            paidByClientAmount: paidByClientAmount ?? 0,
            paidToProviderAmount: paidToProviderAmount ?? 0,
        },
        activities: activities.map((act) => ({
            rid: act.feId,
            bid: act.bid,
            activityTypeId: act.activityTypeId!,
            productId: act.productId!,
            amount: act.amount!,
            unitPrice: act.unitPrice,
            stockId: act.stockId,
            stockFromId: act.stockFromId,
            clientId: act.clientId,
            providerId: act.providerId,
        })),
    }), [companyId, groupBid, activities, groupDescription, paidByClientAmount, paidToProviderAmount]);

    const extractServerErrorMessage = (err: unknown, fallbackMessage: string): string => {
        if (!axios.isAxiosError(err)) {
            return fallbackMessage;
        }

        const responseData: unknown = err.response?.data;

        if (typeof responseData === 'string' && responseData.trim()) {
            return responseData;
        }

        if (responseData && typeof responseData === 'object' && typeof (responseData as { message?: unknown }).message === 'string') {
            return (responseData as { message: string }).message;
        }

        return fallbackMessage;
    };

    const applyValidationErrors = (data: ValidationErrorResponse): string | null => {
        setActivityServerErrors(data.activityErrors ?? {});
        setGeneralServerErrors(data.generalErrors ?? []);
        return data.generalErrors?.[0] ?? null;
    };

    const isFormValid = useCallback(() => {
        if (!operationTypesLoaded) return false;

        return activities.every(act => {
            if (
                act.activityTypeId === undefined ||
                act.productId === undefined ||
                act.amount === undefined ||
                act.amount <= 0
            ) {
                return false;
            }

            const selectedType = activityTypes.find(t => t.id === act.activityTypeId);
            if (!selectedType) return false;

            if (selectedType.requiredFields.clientRequired && !act.clientId) return false;
            if (selectedType.requiredFields.providerRequired && !act.providerId) return false;
            if (selectedType.requiredFields.stockRequired && !act.stockId) return false;

            if (selectedType.operationType === 'BETWEEN_STOCKS' && !act.stockFromId) return false;

            if (
                (selectedType.operationType === 'STOCK_IN' || selectedType.operationType === 'STOCK_OUT')
                 && (act.unitPrice === undefined || act.unitPrice <= 0)
            ) {
                return false;
            }

            return true;
        });
    }, [activities, activityTypes, operationTypesLoaded]);

    // Debounced server-side validation
    useEffect(() => {
        if (!operationTypesLoaded) {
            setValidationError(null);
            setValidationLoading(false);
            setActivityServerErrors({});
            setGeneralServerErrors([]);
            return;
        }

        if (!isFormValid()) {
            setValidationError(null);
            setValidationLoading(false);
            return;
        }

        let isCancelled = false;
        const timeoutId = window.setTimeout(async () => {
            setValidationLoading(true);
            setValidationError(null);

            try {
                await activityService.validateActivityGroup(buildSaveRequest());
                if (!isCancelled) {
                    setActivityServerErrors({});
                    setGeneralServerErrors([]);
                }
            } catch (err: unknown) {
                if (!isCancelled) {
                    if (axios.isAxiosError(err) && err.response?.status === 409) {
                        setValidationError('Конфлікт версій: дані було змінено іншим користувачем. Спробуйте ще раз.');
                    } else if (axios.isAxiosError(err) && err.response?.status === 422) {
                        const generalError = applyValidationErrors(err.response.data);
                        if (generalError) {
                            setValidationError(generalError);
                        }
                    } else {
                        setValidationError(extractServerErrorMessage(err, 'Поточний стан групи не можна зберегти.'));
                    }
                }
            } finally {
                if (!isCancelled) {
                    setValidationLoading(false);
                }
            }
        }, 500);

        return () => {
            isCancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [operationTypesLoaded, isFormValid, buildSaveRequest]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isFormValid() || validationLoading || validationError) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await activityService.saveActivityGroup(buildSaveRequest());
            sessionStorage.removeItem(storageKey);
            onSuccess(response);
        } catch (err: unknown) {
            if (axios.isAxiosError(err) && err.response?.status === 409) {
                setError('Конфлікт версій: дані було змінено іншим користувачем. Спробуйте ще раз.');
            } else if (axios.isAxiosError(err) && err.response?.status === 422) {
                // A general error already renders on its own (generalServerErrors below) — only
                // fall back to this generic banner when there's nothing but field-level errors.
                const generalError = applyValidationErrors(err.response.data);
                if (!generalError) {
                    setError('Деякі активності містять помилки. Перевірте поля нижче.');
                }
            } else {
                setError(extractServerErrorMessage(err, 'Не вдалося зберегти активність'));
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const clearActivityErrors = (feId: string) => {
        if (activityServerErrors[feId]) {
            setActivityServerErrors(prev => {
                const next = { ...prev };
                delete next[feId];
                return next;
            });
        }
    };

    return {
        loading,
        error,
        validationError,
        validationLoading,
        activityServerErrors,
        generalServerErrors,
        isFormValid,
        handleSubmit,
        clearActivityErrors,
    };
}
